'use client';
import { useAriaLabels } from '@/hooks/useAriaLabels';

import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { CMSCard, CMSContentItem } from '@/lib/cms/types';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { devWarn } from '@/lib/utils/devLog';
import { CMSSection } from '../CmsSection';

import styles from './ExpandableColumnStack.module.scss';
import { ExpandableColumnStackItem } from './ExpandableColumnStackItem';
import {
  computeTeleportingIndices,
  extractItemData,
  getControlAriaLabel,
  getControlIcon,
} from './ExpandableColumnStack.utils';

// Supported configuration range. The Figma spec is 3, but 2–5 items render
// with a usable layout (active = 50%, inactive siblings share the other 50%).
// Outside this range the component refuses to render; a dev-only warning is
// emitted so editors and QA can spot the misconfig without the storefront
// surfacing an error message to end users.
const MIN_ITEM_COUNT = 2;
const MAX_ITEM_COUNT = 5;
const IDEAL_ITEM_COUNT = 3;

// Must exceed the .fadeIn CSS transition (0.4s) so the image is fully opaque
// before any video gets paused — prevents the iOS Safari white-flash when
// the browser drops a paused video's frame buffer.
const IMAGE_FADE_IN_MS = 450;

export interface ExpandableColumnStackProps {
  /**
   * Array of content items (CMSCard or CMSContentItem)
   * Each item should have media with both image and video
   */
  items: Array<CMSCard | CMSContentItem | null>;
  /** Optional className */
  className?: string;
  /** CMS index for unique data-testid generation */
  cmsIndex?: number;
}

/**
 * ExpandableColumnStack Component
 *
 * An interactive carousel where one element is "active" (plays video, shows
 * text) and the remaining elements are "inactive" (static image previews).
 * Clicking an inactive element expands it and collapses the previous active.
 *
 * Item count:
 * - The Figma spec and CMS convention is 3 items (see IDEAL_ITEM_COUNT).
 * - The component renders for any N >= 1. With N items, the active element
 *   takes 50% of the row and the remaining (N - 1) inactive elements share
 *   the other 50% equally via flex auto-distribution.
 * - On mobile the carousel shows the active item at 90% width with the next
 *   item peeking from the right; this works for any N >= 2. For N === 1 the
 *   single item simply fills the viewport with no carousel behaviour.
 *
 * @example
 * <ExpandableColumnStack items={[card1, card2, card3]} cmsIndex={5} />
 */
export function ExpandableColumnStack({
  items: rawItems,
  className,
  cmsIndex,
}: ExpandableColumnStackProps) {
  const aria = useAriaLabels();
  // Filter out null items and extract data
  const items = rawItems
    .filter((item): item is CMSCard | CMSContentItem => item !== null)
    .map((item, index) => extractItemData(item, index));

  // Detect mobile for carousel behavior
  const isMobile = useIsMobile();

  // State: which item is currently active (0, 1, or 2)
  const [activeIndex, setActiveIndex] = useState(0);

  // Ref mirror of activeIndex so delayed timeouts can read the latest value
  // without re-creating closures.
  const activeIndexRef = useRef(activeIndex);
  activeIndexRef.current = activeIndex;

  // State: is the active video playing or paused
  const [isPlaying, setIsPlaying] = useState(true);

  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;

  // Indices of videos confirmed playable — prevents fading the image out
  // to reveal a grey placeholder when no video source is available.
  const [readyVideos, setReadyVideos] = useState<Set<number>>(() => new Set());

  // State: track if transition has completed (for showing text)
  const [transitionComplete, setTransitionComplete] = useState(true);

  // Refs for video elements
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  // Ref for transition timeout
  const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Ref for delayed video pause (lets the image fade in before pausing)
  const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Ref for swipe gesture tracking
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Calculate next (peeking) and previous indices for continuous loop
  const nextIndex = (activeIndex + 1) % items.length;
  const _prevIndex = (activeIndex - 1 + items.length) % items.length;

  // Track which items need their CSS transition disabled for one frame so
  // they can jump ("teleport") between positions without sliding across the
  // screen. May contain 0..N-3 items depending on the number of items in the
  // stack and the direction of the transition.
  const [teleportingIndices, setTeleportingIndices] = useState<ReadonlySet<number>>(
    () => new Set()
  );

  // Get mobile position class for an item
  const getMobilePositionClass = (index: number): string | undefined => {
    if (!isMobile) return undefined;
    if (index === activeIndex) return undefined; // Active uses .active class
    if (index === nextIndex) return styles.mobileNext;
    return styles.mobilePrev; // Previous item slides off to the left
  };

  // Check if item should have no transition (teleporting)
  const shouldSkipTransition = (index: number): boolean => {
    return isMobile && teleportingIndices.has(index);
  };

  // Generate test ID prefix
  const testIdPrefix = cmsIndex !== undefined ? `expandable-stack-${cmsIndex}` : 'expandable-stack';

  // Handle clicking on an inactive item to expand it
  const handleItemClick = useCallback(
    (index: number) => {
      if (index !== activeIndex) {
        // Clear any existing timeouts
        if (transitionTimeoutRef.current) {
          clearTimeout(transitionTimeoutRef.current);
        }
        if (pauseTimeoutRef.current) {
          clearTimeout(pauseTimeoutRef.current);
        }

        // Hide text immediately when transition starts
        setTransitionComplete(false);

        // On mobile, any item transitioning between `mobilePrev` (left: -90%)
        // and `mobileNext` (left: 90%) must teleport — otherwise it slides
        // horizontally across the full viewport on top of (or behind) the
        // other items, which looks broken. Adjacent transitions
        // (active ↔ next, active ↔ prev) animate smoothly and never teleport.
        //
        // For N=3 exactly one item teleports per transition. For N >= 4 still
        // exactly one item moves between prev ↔ next per adjacent transition,
        // but it is NOT always `oldPrevIndex` or `oldNextIndex`, so the
        // previous shortcut produced the wrong item and the offending slide
        // played its full horizontal animation. N=2 never needs a teleport.
        if (isMobile && items.length >= 3) {
          const teleports = computeTeleportingIndices(activeIndex, index, items.length);
          if (teleports.size > 0) {
            setTeleportingIndices(teleports);
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                setTeleportingIndices(new Set());
              });
            });
          }
        }

        setActiveIndex(index);
        setIsPlaying(true); // Auto-play when expanding

        // Show text after transition completes
        // Desktop: 1s transition, Mobile: 0.5s transition
        const transitionDuration = isMobile ? 550 : 1050;
        transitionTimeoutRef.current = setTimeout(() => {
          setTransitionComplete(true);
        }, transitionDuration);
      }
    },
    [activeIndex, isMobile, items.length]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
    };
  }, []);

  // Go to next item (for mobile carousel)
  const goToNext = useCallback(() => {
    const nextIndex = (activeIndex + 1) % items.length;
    handleItemClick(nextIndex);
  }, [activeIndex, items.length, handleItemClick]);

  // Go to previous item (for mobile carousel)
  const goToPrev = useCallback(() => {
    const prevIndex = (activeIndex - 1 + items.length) % items.length;
    handleItemClick(prevIndex);
  }, [activeIndex, items.length, handleItemClick]);

  // Handle touch start for swipe gesture
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  }, []);

  // Handle touch end for swipe gesture
  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStartRef.current || !isMobile) return;

      const touchEnd = {
        x: e.changedTouches[0].clientX,
        y: e.changedTouches[0].clientY,
      };

      const deltaX = touchEnd.x - touchStartRef.current.x;
      const deltaY = touchEnd.y - touchStartRef.current.y;

      // Only register horizontal swipes (not vertical scrolling)
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
        if (deltaX < 0) {
          // Swipe left -> go to next
          goToNext();
        } else {
          // Swipe right -> go to previous
          goToPrev();
        }
      }

      touchStartRef.current = null;
    },
    [isMobile, goToNext, goToPrev]
  );

  // Handle play/pause toggle for active item.
  // On pause, the image fades in FIRST (0.4s CSS transition) while the video
  // keeps playing underneath. The actual video.pause() is delayed until the
  // image is fully opaque, preventing the white flash caused by iOS Safari
  // dropping the paused video's frame buffer.
  const handlePlayPause = useCallback(() => {
    const video = videoRefs.current[activeIndex];
    if (!video) return;

    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
      pauseTimeoutRef.current = null;
    }

    if (isPlaying) {
      setIsPlaying(false);
      pauseTimeoutRef.current = setTimeout(() => {
        video.pause();
        pauseTimeoutRef.current = null;
      }, IMAGE_FADE_IN_MS);
    } else {
      video.play();
      setIsPlaying(true);
    }
  }, [activeIndex, isPlaying]);

  // Mark a video as playable once the browser has loaded enough data.
  // If this video is currently active and in "playing" mode, kick off
  // playback immediately so the image can fade out.
  const handleVideoCanPlay = useCallback((index: number) => {
    setReadyVideos((prev) => {
      if (prev.has(index)) return prev;
      const next = new Set(prev);
      next.add(index);
      return next;
    });
    if (index === activeIndexRef.current && isPlayingRef.current) {
      videoRefs.current[index]?.play().catch(() => {});
    }
  }, []);

  // Sync video playback when active index or play state changes.
  // Non-active video pauses are delayed so the cover image can fade in first,
  // preventing the iOS Safari white-flash on paused videos.
  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (!video) return;

      if (index === activeIndex && isPlaying) {
        video.play().catch(() => {});
      } else if (index !== activeIndex) {
        setTimeout(() => {
          if (activeIndexRef.current !== index) {
            video.pause();
            video.currentTime = 0;
          }
        }, IMAGE_FADE_IN_MS);
      }
    });
  }, [activeIndex, isPlaying]);

  if (items.length < MIN_ITEM_COUNT || items.length > MAX_ITEM_COUNT) {
    devWarn(
      `ExpandableColumnStack: ${items.length} item(s) configured. ` +
        `This component requires between ${MIN_ITEM_COUNT} and ${MAX_ITEM_COUNT} items (ideal: ${IDEAL_ITEM_COUNT}).`
    );
    return null;
  }

  return (
    <CMSSection className={className} data-testid={`${testIdPrefix}-section`} withMargin={false}>
      <div className={styles.container}>
        <div
          ref={wrapperRef}
          className={styles.wrapper}
          data-testid={`${testIdPrefix}-wrapper`}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {items.map((item, index) => {
            const isActive = index === activeIndex;
            const videoUrl = isMobile
              ? (item.videoMobileUrl ?? item.videoUrl)
              : (item.videoUrl ?? item.videoMobileUrl);
            const slideLabel = item.title || `slide ${index + 1}`;
            const verb = isMobile ? 'Go to' : 'Expand';

            return (
              <ExpandableColumnStackItem
                key={item.id}
                item={item}
                index={index}
                isActive={isActive}
                isPlaying={isActive && isPlaying}
                canPlayVideo={readyVideos.has(index)}
                transitionComplete={transitionComplete}
                videoUrl={videoUrl ?? null}
                mobilePositionClass={getMobilePositionClass(index)}
                skipTransition={shouldSkipTransition(index)}
                testIdPrefix={testIdPrefix}
                itemAriaLabel={isActive ? undefined : `${verb} ${slideLabel}`}
                controlAriaLabel={getControlAriaLabel(isActive, isPlaying, item.title, index)}
                controlIcon={getControlIcon(isActive, isPlaying)}
                videoRef={(el) => {
                  videoRefs.current[index] = el;
                }}
                onItemClick={handleItemClick}
                onPlayPause={handlePlayPause}
                onVideoCanPlay={handleVideoCanPlay}
              />
            );
          })}
        </div>

        {/* Mobile pagination dots */}
        <div className={styles.pagination} aria-label={aria.action('carouselNavigation')}>
          {items.map((item, index) => (
            <button
              key={`dot-${item.id}`}
              className={clsx(styles.dot, index === activeIndex && styles.dotActive)}
              onClick={() => handleItemClick(index)}
              aria-label={`Go to slide ${index + 1}`}
              aria-current={index === activeIndex ? 'true' : undefined}
            />
          ))}
        </div>
      </div>
    </CMSSection>
  );
}

ExpandableColumnStack.displayName = 'ExpandableColumnStack';
